/* global HSCore */
/* global bootstrap */

import React, { useContext, useEffect, useRef, useState } from "react";
import axiosInstance from "../utils/Requests/axios";
import UserContext from "../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import LanguageContext from "../utils/Contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import ArrowUpIcon from "../components/svg/ArrowUp";
import ThemeContext from "../utils/Contexts/ThemeContext";

function ImportModal({ fetchImportsData, flag }) {
  const { userData } = useContext(UserContext);
  const { selectedTheme } = useContext(ThemeContext);
  const { t } = useContext(LanguageContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const modalRef = useRef(null);
  const hsInitRef = useRef(null);
  const [isChecked, setIsChecked] = useState(false);
  const [disableUpload, setDisableUpload] = useState(true);
  const navigate = useNavigate();

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  useEffect(() => {
    if (!hsInitRef.current) {
      HSCore.components.HSDropzone.init(".js-dropzone", {
        url: '/',
        autoProcessQueue: false,
      });

      const dropzoneEl = document.querySelector(".js-dropzone");
      const dzInstance = dropzoneEl?.dropzone;

      if (dzInstance) {
        dzInstance.on("addedfile", (file) => {
          setSelectedFile(file);
          const existingFileElements = document.querySelectorAll(".dz-preview");

          const successMarks = document.querySelectorAll(".dz-success-mark");
          successMarks.forEach(mark => {
            mark.style.display = 'none';
          });

          const currentSuccessMark = existingFileElements[existingFileElements.length - 1]?.querySelector(".dz-success-mark");
          if (currentSuccessMark) {
            currentSuccessMark.style.display = 'block';
          }

          if (existingFileElements.length > 0) {
            setDisableUpload(false);
          }

          if (existingFileElements.length > 1) {
            const removeButton = existingFileElements[0].querySelector("[data-dz-remove]");
            if (removeButton) {
              removeButton.click();
            }
          }
        });

        dzInstance.on("error", (file) => {
          const errorMarks = document.querySelectorAll(".dz-error-mark");
          errorMarks.forEach(mark => {
            mark.style.display = 'none';
          });

          const currentErrorMark = file.previewElement.querySelector(".dz-error-mark");
          if (currentErrorMark) {
            currentErrorMark.style.display = 'block';
          }
        });
      }
      hsInitRef.current = true;
    }
  }, []);

  const postData = () => {
    if (!selectedFile) {
      toast.error(t("Please select a file to upload"));
      return;
    }

    if (!isChecked) {
      toast.error(t("Please acknowledge that the import process will overwrite existing offers or terms."));
      return;
    }

    const url = `/imports/products/100/sellers/${userData?.uuid}`;

    const formData = new FormData();
    formData.append("file", selectedFile);

    axiosInstance
      .post(url, formData)
      .then(() => {
        toast.success(t("File uploaded successfully"));
        const modal = bootstrap.Modal.getInstance(modalRef.current);
        modal.hide();
        const dropzoneElement = document.querySelector(".js-dropzone");
        const removeButton = dropzoneElement?.querySelector("[data-dz-remove]");
        if (removeButton) {
          removeButton.click();
        }
        setSelectedFile(null);
        if (flag) {
          navigate("/offers/import");
        } else {
          fetchImportsData();
        }
      })
      .catch(() => {
        toast.error(t("There was an error uploading file."));
      });
  };

  return (
    <div
      ref={modalRef}
      className="modal fade import-modal"
      id="importModal"
      tabIndex="-1"
      aria-labelledby="importModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="importModalLabel">
              {t("Import Offers or Website Terms")}
            </h3>
          </div>

          <div className="modal-body py-3">
            <p>
              {t("Download sample CSV templates for")}{" "}
              <button
                className="btn btn-outline-secondary py-0 rounded-3 bg-soft-secondary text-dark px-2 me-2"
                style={{ fontWeight: "unset" }}
                href="/"
              >
                {t("Offer Import")}
                <i style={{ marginLeft: "5px" }}>{" "}
                  <ArrowUpIcon
                    fill={selectedTheme === "dark" ? "#fff" : "#8c98a4"}
                  />
                </i>
              </button>
              <span>{t("or")}</span>
              <button
                className="btn btn-outline-secondary py-0 rounded-3 bg-soft-secondary text-dark px-2 ms-2"
                style={{ fontWeight: "unset" }}
                href="/"
              >
                {t("Offer Terms Import")}
                <i style={{ marginLeft: "5px" }}>{" "}
                  <ArrowUpIcon fill={selectedTheme === "dark" ? "#fff" : "#8c98a4"} />
                </i>
              </button><br />
              {t("to view the required format.")}
            </p>

            <div
              className="pt-3"
              style={{
                borderTop: `1px solid ${selectedTheme === "dark" ? '#2f3235' : 'rgba(231, 234, 243, 0.7)'}`,
              }}
            >
              <div
                id="attachFilesNewProjectLabel"
                className="js-dropzone dz-dropzone dz-dropzone-card mb-4"
              >
                <div className="dz-message">
                  <p>{t("Drag and drop your file here")}</p>
                  <p className="mb-2">{t("or")}</p>
                  <span className="btn btn-outline-primary btn-sm">
                    {t("Browse files")}
                  </span>
                </div>
              </div>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="overwriteCurrentOffersCheckbox"
                onChange={handleCheckboxChange}
              />
              <label
                className="form-check-label"
                htmlFor="overwriteCurrentOffersCheckbox"
              >
                {t(
                  "I acknowledge that the import process will overwrite my existing offers or terms."
                )}{" "}
                <button
                  className="btn btn-outline-secondary py-0 rounded-3 bg-soft-secondary text-dark px-2"
                  style={{ fontWeight: "unset" }}
                  href="/"
                >
                  {t("Learn more")}{" "}
                  <i style={{ marginLeft: "5px" }}>
                    <ArrowUpIcon fill={selectedTheme === "dark" ? "#fff" : "#8c98a4"}/>
                  </i>
                </button>
              </label>
            </div>
          </div>

          <div className="modal-footer mx-5 px-0">
            <button
              type="button"
              className="btn btn-outline-primary px-6"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={postData}
              disabled={disableUpload}
            >
              {t("Upload and Continue")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportModal;
